/**
 * Planification de la piste d'une nouvelle remontée. Partagée : le client
 * l'appelle pour l'aperçu en mode construction, le serveur pour la
 * construction réelle, avec les mêmes paramètres.
 */
import type { LiftKind } from './catalog';
import type { Vec2 } from './geometry';
import { computeLiftGeometry, queueSlot, topExit } from './lifts';
import { analysePiste, generatePistePath, TARGET_GRADE, type PisteAnalysis } from './pistes';
import type { Difficulty } from './state';
import type { Heightfield } from './terrain';

export interface PistePlan {
  path: Vec2[];
  analysis: PisteAnalysis;
}

export function planLiftPiste(
  hf: Heightfield,
  otherLifts: Iterable<{ bottom: Vec2; top: Vec2 }>,
  kind: LiftKind,
  bottom: Vec2,
  top: Vec2,
  difficulty: Difficulty | 'auto',
): PistePlan {
  const geo = computeLiftGeometry(hf, kind, bottom, top);
  const from = topExit(geo);
  const to = queueSlot(geo, 14);
  const avoid = [...otherLifts].map((l) => ({ a: l.bottom, b: l.top, clearance: 26 }));
  avoid.push({ a: bottom, b: top, clearance: 24 });
  const grade = difficulty === 'auto' ? Math.max(0.12, Math.min(0.42, geo.avgSlope * 0.9)) : TARGET_GRADE[difficulty];
  let best: (PistePlan & { score: number }) | null = null;
  for (const side of [1, -1] as const) {
    const path = generatePistePath(hf, from, to, { side, grade, seed: side, avoid });
    const analysis = analysePiste(hf, path);
    let score = analysis.uphill * 2 + Math.abs(analysis.avgSlope - grade) * 10;
    if (difficulty !== 'auto' && analysis.difficulty !== difficulty) score += 3;
    if (!best || score < best.score) best = { path, analysis, score };
  }
  const b = best as PistePlan & { score: number };
  return { path: b.path, analysis: b.analysis };
}

/** Une descente qui remonte de plus de 8 m n'est pas skiable. */
export const MAX_UPHILL = 8;
